import { Outlet, useLocation } from "react-router-dom";
import { SidebarNew } from "./SidebarNew";
import { NavbarNew } from "./NavBarNew";
import { useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "@/hooks/useUser";
import { useTranslation } from "react-i18next";
// import useCurruntLocationQuary from "@/api/query/useCurruntLocationQuary";
import useRibbonsListQuery from "@/api/query/useRibbonMessageQuery";
import { darkenColor } from "../views/custom/gantt/src/helpers/colorMixer";
import { getTimeRemaining } from "@/helperFuntions/timeRemaining";
import {
  PlanTypeEnumSchema,
  UserRoleEnumValue,
} from "@backend/src/schemas/enums";
import useProjectWithoutCalulationQuery from "@/api/query/useProjectWithoutCalulationQuery";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { ErrorFallback } from "../common/ErrorFallback";

const LayoutErrorFallback = (props: FallbackProps) => (
  <ErrorFallback {...props} standalone={false} />
);

interface LayoutProps {
  children?: React.ReactNode;
}

function Layout({ children }: LayoutProps) {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();
  const { user } = useUser();
  // const locationQuary = useCurruntLocationQuary();
  const projectQuery = useProjectWithoutCalulationQuery(!!user);

  // useEffect(() => {
  //   if (user) {
  //     i18n.changeLanguage(
  //       "fr"
  //       // locationQuary.data?.data.data?.country === "CA" || locationQuary.data?.data.data?.country === "FR" ? "fr" : "en"
  //     );
  //   }
  // }, [locationQuary, user]);

  const ribbonsListQuery = useRibbonsListQuery();
  const [showRibbon, setShowRibbon] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setShowRibbon(setShow());
  }, [ribbonsListQuery.isRefetching, dismissed, user]);

  const setShow = () => {
    if (
      user &&
      user.userOrganisation[0]?.role == UserRoleEnumValue.ADMINISTRATOR
    ) {
      if (
        user.subscription?.planType == PlanTypeEnumSchema.ENTERPRISE ||
        user.subscription?.planType == PlanTypeEnumSchema.BUSINESS
      ) {
        return false;
      }
      if (
        !projectQuery.data?.data.data?.length &&
        location.pathname == "/projects"
      ) {
        return false;
      }

      if (!ribbonsListQuery.data?.data?.data?.[0]?.active) {
        return false;
      }
      if (ribbonsListQuery.data?.data?.data?.[0]) {
        const today = new Date();
        const ribbon = ribbonsListQuery.data.data.data[0];

        const ribbonStartDate = new Date(ribbon.startDate);
        const ribbonExpiry = new Date(ribbon.validUntil);

        today.setHours(0, 0, 0, 0);
        ribbonStartDate.setHours(0, 0, 0, 0);
        ribbonExpiry.setHours(0, 0, 0, 0);

        const isTodayStart = today.getTime() === ribbonStartDate.getTime();
        const isTodayExpiry = today.getTime() === ribbonExpiry.getTime();
        const isWithinRange = today >= ribbonStartDate && today <= ribbonExpiry;

        return (isWithinRange || isTodayStart || isTodayExpiry) && !dismissed;
      }
    }
    return false;
  };

  const [time, setTime] = useState<{ [key: string]: string }>();
  const validUntil = ribbonsListQuery.data?.data.data[0]?.validUntil ?? "";

  useEffect(() => {
    const interval = setInterval(() => {
      if (validUntil) {
        const remainingTime = getTimeRemaining(validUntil);
        setTime(remainingTime);
      }
    }, 1000);

    return () => clearInterval(interval); // Cleanup to avoid memory leaks
  }, [validUntil]); // Depend on validUntil instead of ribbonsListQuery.isRefetching

  const memoizedRibbon = useMemo(() => {
    return (
      <div className="w-[85%]">
        <div
          className="ribbon w-full z-50 relative flex justify-center gap-2"
          style={
            {
              "--ribbon-color":
                ribbonsListQuery.data?.data.data[0]?.backgroundColor,
              "--ribbon-color-dark": darkenColor(validUntil),
            } as React.CSSProperties
          }
        >
          <div
            className="text-center h-fit my-auto"
            dangerouslySetInnerHTML={{
              __html: ribbonsListQuery.data?.data.data[0]?.message ?? "",
            }}
          />
          <div className="text-white w-40 flex gap-2 items-center">
            {time &&
              Object.keys(time).map((t) => {
                return (
                  <div className="" key={t}>
                    <div className="text-lg font-semibold">{time[t] ?? 0}</div>
                    <div className="text-[0.5rem] font-semibold tracking-widest">
                      {t}
                    </div>
                  </div>
                );
              })}
          </div>
          <div
            className="text-xs text-right absolute right-1 bottom-1 text-white !font-bold underline cursor-pointer"
            onClick={() => {
              localStorage.setItem("ribbon", validUntil);
              setShowRibbon(false);
              setDismissed(true);
            }}
          >
            {t("dismiss")}
          </div>
        </div>
      </div>
    );
  }, [time, validUntil]);

  const useShowPopup = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);

    const notAllowRibbon = ["plans", "checkout", "organisation"];
    const allowOnlyDynamic = ["organisation"];

    if (allowOnlyDynamic.includes(pathSegments[0]) && pathSegments.length > 1) {
      return true;
    }
    if (notAllowRibbon.includes(pathSegments[0])) {
      return false;
    }

    return true;
  };
  const outletRef = useRef<HTMLDivElement | null>(null);
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const layoutGridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Layout observer removed as CSS handles it smoothly now
  }, [open]);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    const handler = () => {
      setIsFullScreen(!!document.fullscreenElement);
      if (document.fullscreenElement) {
        // Fullscreen handled by CSS classes
      }
    };

    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  return (
    <div className="h-screen w-full  overflow-hidden" ref={layoutRef}>
      {/* GRID CONTAINER */}
      <div
        ref={layoutGridRef}
        className={`
          grid h-screen gap-2 duration-300 ease-in-out
          ${isFullScreen ? "grid-cols-1" : "grid-cols-[250px_1fr]"}
          grid-rows-[auto_1fr]
        `}
      >
        {/* SIDEBAR */}
        {!isFullScreen && (
          <div className={`col-start-1 row-start-1 z-20 ${open ? "row-span-2" : "row-span-1 h-[58px]"}`}>
            <SidebarNew isShow={open} setOpen={setOpen} />
          </div>
        )}

        {/* NAVBAR */}
        {!isFullScreen && (
          <div className="col-start-2 row-start-1">
            <NavbarNew isShow={open} setOpen={setOpen} />
          </div>
        )}

        {/* RIBBON */}
        {useShowPopup() && showRibbon && (
          <div className="col-start-2 row-start-2 bg-slate-100 flex justify-center">
            {memoizedRibbon}
          </div>
        )}

        {/* CONTENT */}
        <div
          className={`
            col-span-full row-start-2 overflow-auto relative [&::-webkit-scrollbar:horizontal]:h-0 [&::-webkit-scrollbar:horizontal]:hidden [-ms-overflow-style:none] transition-all duration-300 ease-in-out
            col-start-1
          `}
          style={{ paddingLeft: (!isFullScreen && open) ? "250px" : "0px" }}
        >
          <div ref={outletRef} className="h-full w-full right-0">
            {children ?? (
              <ErrorBoundary
                FallbackComponent={LayoutErrorFallback}
                resetKeys={[location.pathname]}
                onReset={() => {
                  window.location.reload();
                }}
              >
                <Outlet />
              </ErrorBoundary>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Layout;
